import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icone } from './Icone'

/**
 * La modale de la console — TRANSPOSITION du bloc `#modal-bg` d'origine.
 *
 * ⚠️ ELLE PASSE PAR UN PORTAIL VERS `document.body`, et ce n'est pas un détail
 * d'implémentation React : c'est la transposition littérale de l'original, où
 * `.modal-bg` est un élément UNIQUE posé à la fin du `<body>`, hors de `#app`.
 *
 * Sans portail, le composant se rendrait là où il est appelé — à l'intérieur
 * d'une carte. Or toutes les cartes de cette console portent `data-anim`, et
 * la règle `[data-anim]{animation:fadeUp .32s var(--ease-out) both}` s'applique
 * à elles. `fadeUp` finit sur `transform:none`, mais `animation-fill-mode:both`
 * garde l'animation APPLIQUÉE : la valeur calculée n'est pas le mot-clé `none`,
 * c'est la matrice identité — qui reste un transform. La carte devient donc
 * bloc conteneur de tout `position:fixed` qu'elle abrite, POUR TOUJOURS.
 *
 * La modale s'ouvrirait alors à la taille de la carte, souvent sous la ligne
 * de flottaison. C'est exactement le défaut rencontré sur la console SwiftEats,
 * et rien — ni le compte de classes, ni le typage, ni la console du navigateur
 * — ne le signale : la modale EST là, elle est simplement au mauvais endroit.
 */
export function Modale({
  titre,
  ouverte,
  surFermer,
  surEnregistrer,
  libelleEnregistrer = 'Enregistrer',
  enCours = false,
  desactive = false,
  large = false,
  children,
}: {
  titre: string
  ouverte: boolean
  surFermer: () => void
  /** Absent = modale de consultation : le pied n'affiche que « Fermer ». */
  surEnregistrer?: () => void
  libelleEnregistrer?: string
  enCours?: boolean
  /** Bouton d'enregistrement grisé SANS afficher « Enregistrement… » (rien à enregistrer pour l'instant). */
  desactive?: boolean
  /** Modale large (tableaux) : 900 px au lieu de 520. */
  large?: boolean
  children: ReactNode
}) {
  /* Échap ferme — réflexe attendu de toute superposition. */
  useEffect(() => {
    if (!ouverte) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && surFermer()
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [ouverte, surFermer])

  return createPortal(
    <div
      className={`modal-bg${ouverte ? ' on' : ''}`}
      /* Le clic sur le fond ferme, celui sur la boîte non — sans ce test, un
         clic n'importe où DANS le formulaire fermerait la saisie en cours. */
      onClick={(e) => e.target === e.currentTarget && surFermer()}
    >
      <div className={`modal${large ? ' modal-large' : ''}`}>
        <div className="modal-head">
          <h3>{titre}</h3>
          <button className="x" onClick={surFermer} aria-label="Fermer">
            <Icone nom="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">
          <button className="btn btn-outline" onClick={surFermer}>
            {surEnregistrer ? 'Annuler' : 'Fermer'}
          </button>
          {surEnregistrer && (
            <button className="btn btn-gold" onClick={surEnregistrer} disabled={enCours || desactive}>
              {enCours ? 'Enregistrement…' : libelleEnregistrer}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
